import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { usePermissions } from '../context/PermissionsContext';
import '../styles/Login.css';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const { refreshPermissions } = usePermissions();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/login`, formData);
      const { access_token, user } = response.data;

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));

      // Refresh permissions after login
      refreshPermissions();

      toast.success(`Welcome back, ${user.full_name}!`);
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page" data-testid="login-page">
      <div className="login-container fade-in">
        {/* Left Side - Branding */}
        <div className="login-brand" data-testid="login-brand">
          <div className="brand-content">
            <h1 className="brand-title" style={{ color: '#E7EDF9' }}>CNESS Hive</h1>
            <p className="brand-tagline">Your productivity partner for balanced excellence</p>
            <div className="brand-features">
              <div className="brand-feature">
                <div className="feature-dot"></div>
                <span>Track six dimensions of productivity</span>
              </div>
              <div className="brand-feature">
                <div className="feature-dot"></div>
                <span>Data-driven insights and analytics</span>
              </div>
              <div className="brand-feature">
                <div className="feature-dot"></div>
                <span>Seamless team collaboration</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="login-form-container" data-testid="login-form-container">
          <div className="login-form-content">
            <div className="form-header">
              <h2 data-testid="login-form-title">Welcome Back</h2>
              <p data-testid="login-form-subtitle">Sign in to your account to continue</p>
            </div>

            <form onSubmit={handleSubmit} className="login-form" data-testid="login-form">
              <div className="form-group">
                <label htmlFor="email" data-testid="login-email-label">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  required
                  data-testid="login-email-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password" data-testid="login-password-label">Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    data-testid="login-password-toggle"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div style={{ textAlign: 'right', marginBottom: '16px' }}>
                <a 
                  href="/forgot-password" 
                  style={{ 
                    color: 'var(--color-primary)', 
                    fontSize: '14px',
                    textDecoration: 'none'
                  }}
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </a>
              </div>

              <button
                type="submit"
                className="login-btn gradient-btn"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <span>Signing in...</span>
                ) : (
                  <>
                    <LogIn size={20} />
                    <span>Sign In</span>
                  </>
                )}
              </button>
            </form>

            <div className="form-footer">
              <a href="/" className="back-link" data-testid="login-back-link">← Back to Home</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;